export default {
  Common: {
    loading: 'Loading...',
  },
  errors: {
    session_create_error: 'Failed to create session.\nPlease try again.',
    session_continue_error:
      "We couldn't load your session. Please try again. If the problem persists, contact support at service{'@'}theartisan.ai.",
    kiosk_not_found: 'Failed to load kiosk.\nPlease try again.',

    art_style_missing: 'No art styles have been\n added to this kiosk',
    art_style_fetch_error: 'Failed to load art style',
    art_style_preload_error: 'Failed to load art style preview',

    face_detection_server_error: 'Failed to process photo. Please try again.',
    face_detection_preload_error: 'Error preloading detected faces',
    face_detection_no_faces:
      'No faces detected. Please try taking another photo.',

    photo_webcam_not_allowed: 'Webcam permission denied',
    photo_webcam_not_found:
      "Webcam not found. Please check if it's connected to the kiosk.",
    photo_webcam_hardware_error:
      'Webcam is busy or not working.\nPlease try again or ask for assistance.',
    photo_webcam_photo_failed: 'Failed to take a photo\n with the webcam',

    photo_phone_missing_session_id:
      "We couldn't find your session.\nPlease scan the QR code again.",
    photo_phone_missing_kiosk_slug:
      "Kiosk not found in your session. If the problem persists, contact support at service{'@'}theartisan.ai.",
    photo_phone_limit_exceeded: 'Too many photos selected',
    photo_phone_qr_error: 'Failed to generate QR code\nfor photo upload',

    photo_file_unsupported_type: 'Unsupported file type',
    photo_file_cannot_process:
      'Unable to process this photo.\nPlease try another one.',
    photo_file_cannot_process_heic:
      'Unable to process HEIC photo.\nPlease try another one.',

    portrait_generation_error:
      'An error occurred while generating your portrait. Please try again.',
    portrait_generation_faces_not_selected:
      'No faces selected for\nportrait generation',
    portrait_generation_style_not_selected:
      'No art style selected for\nportrait generation',
    portrait_generation_over_limit: 'Too many generation attempts',

    portraits_download_session_error:
      "We couldn't find your session. Please check the code you entered. If the problem persists, contact support at service{'@'}theartisan.ai.",
    portraits_download_kiosk_error:
      "Kiosk not found in your session. If the problem persists, contact support at service{'@'}theartisan.ai.",
    portraits_download_portraits_error:
      "No portraits found in your session. If the problem persists, contact support at service{'@'}theartisan.ai.",

    payment_partner_mode_missing_stripe_account:
      'Partner payment mode requires a Stripe Connect account. Please configure the partner Stripe account or switch to platform payment mode.',

    api_error_missing_session_id: 'Session not found',
    api_error_email_code_validation:
      'Failed to validate payment code. Please try again.',
  },

  /**
   * Global components
   */

  OverlayError: {
    buttons: {
      close: 'Dismiss',
      restart: 'Start Over',
      retry: 'Try Again',
    },
  },
  Logo: {
    startOver: 'Start Over',
  },
  Copyright: {
    copyright: 'powered by TheArtisan.ai',
    distribution: 'distributed by',
    terms:
      'By taking a picture on this device, you accept our policies and terms. To review them, please',
    termsLink: 'click here',
  },
  IdlePopup: {
    title: 'Are you still here?',
    caption: "Your session will end if you're inactive.",
    buttonContinue: "Yes, I'm still here",
    buttonRestart: 'No, Start Over',
  },
  GDPRConsent: {
    title: 'Consent Required',
    consentText:
      'We need your consent to process your facial features (biometric data) to create artistic transformations. You may only proceed if you are 16 or older (or have parental consent).',
    buttonConsent: 'I Consent',
    buttonDecline: 'I Do Not Consent',
    privacyPolicyLink: 'View Privacy Policy',
    declineMessage: 'You cannot proceed without consent',
  },
  StepProgressIndicator: {
    photoShort: 'Photo',
    photoLong: 'Take Photo',
    facesShort: 'Faces',
    facesLong: 'Choose Faces',
    groupPortraitsShort: 'Group',
    groupPortraitsLong: 'Group Portraits',
    portraitsShort: 'Portraits',
    portraitsLong: 'Choose Portraits',
    printShort: 'Print',
    printLong: 'Pay & Print',
    sendToPickupShort: 'Pickup',
    sendToPickupLong: 'Send to Pickup',
  },
  UploadedPhotosSidebar: {
    title: 'Your Photos',
    count: '+{extraPhotoCount} more',
  },
  NumericKeyboard: {
    invalidPin: 'Invalid PIN code',
    clear: 'Clear',
    enter: 'Enter',
  },
  OutOfService: {
    message:
      'Your contract appears to be paused or expired.\nPlease contact your distributor for support.',
  },

  /**
   * Steps
   */

  Home: {
    title: 'Generate your portrait in seconds!',
    button: 'Try Now!',
  },
  ArtStyle: {
    title: 'Choose Your Style',
  },
  PhotoFromWebcam: {
    titleGetReady: 'Get ready to take a photo',
    titleAttention: 'Look at the camera!',
    titleReview: 'Do you like this photo?',
    titleUpload: 'Uploading your photo…',
    buttonPrimaryPhoto: 'Take a Photo',
    buttonPrimaryUse: 'Yes, Use This Photo',
    buttonPrimaryRetake: 'Retake Photo',
    buttonSecondaryUpload: 'Upload From Phone',
    buttonSecondaryRetake: 'No, Retake (1 Attempt Left)',
    buttonSecondaryUseFirst: 'No, Use the First Photo',
    titleCameraError: 'Camera unavailable',
    buttonRetryCamera: 'Retry Camera',
  },
  PhotoPromptFromPhone: {
    title: 'Upload Photos From Phone?',
    caption:
      'Surprise friends and family —\nupload their photos to create portraits.',
    buttonUpload: 'Yes, Upload From Phone',
    buttonSkip: 'No, Skip Phone Upload',
  },
  PhotoFromPhone: {
    title: 'Upload photos from your phone',
    step1: 'Scan QR code with your phone',
    step2: 'Select photos',
    step3: 'Wait for the upload',
    waiting: 'Waiting for your upload…',
    done: 'Upload successful',
    buttonCancel: 'Cancel & Go Back',
    buttonSkip: 'Skip Phone Upload',
    buttonContinue: 'Continue',
    gdprConsentAll:
      'I have consent from everyone in this photo to process their facial features',
    gdprOver16: 'No one in this photo is under 16 years old',
    gdprResponsibility: 'I accept full responsibility for GDPR compliance',
    gdprAgreeToPrefix: 'I agree to the',
    gdprPrivacyPolicyLink: 'Privacy Policy',
    gdprAgreeToSuffix: 'terms for uploaded photos',
    gdprCheckboxesRequired: 'Please check all boxes below to continue',
    buttonSkipKioskPhoto: 'Skip photo from kiosk',
    buttonTakeWebcamPhoto: 'Take photo from webcam',
  },
  FacesIndividual: {
    title: 'Pick who will be on the portraits?',
    buttonConfirm: 'Confirm & Continue',
    genderMale: 'Male',
    genderFemale: 'Female',
    genderNonBinary: 'Non-Binary',
    age0to2: '0-2',
    age3to9: '3-9',
    age10to19: '10-19',
    age20to29: '20-29',
    age30to39: '30-39',
    age40to49: '40-49',
    age50to59: '50-59',
    age60to69: '60-69',
    age70plus: '70+',
    raceWhite: 'White',
    raceBlack: 'Black',
    raceEastAsian: 'East Asian',
    raceSoutheastAsian: 'Southeast Asian',
    raceIndian: 'Indian',
    raceLatino: 'Latino',
    raceMena: 'Middle Eastern',
  },
  Portraits: {
    title: 'Pick your portraits!',
    buttonNext: 'Next: Print Selected',
    buttonNextGroupOnly: 'Continue with Group Portraits Only',
    galleryScrollBack: 'Previous portraits',
    galleryScrollForward: 'Next portraits',
    filterLabel: 'Show',
    filterAll: 'All',
    filterGroup: 'Together',
    filterIndividual: 'Solo',
    stageKindGroup: 'Group portrait',
    stageKindIndividual: 'Portrait',
    stagePosition: '{index} of {count}',
    stageSelect: 'Select',
    stageSelected: 'Selected ✓',
    buttonMore: 'Make more portraits ({count} left)',
    popupTitle: 'Do you want to finish your order?',
    popupGroupSelected: 'Group portraits selected: {count} ({price})',
    popupIndividualSelected: 'Individual portraits selected: {count} ({price})',
    popupTotalSum: 'Total sum: {sum}',
    popupButtonNo: 'No, go back',
    popupButtonYes: 'Yes, print & pay!',
    error: 'Oops! Couldn’t generate this portrait.',
    placeholderGenerating: 'Generating your portrait…',
    placeholderLoading: 'Loading your portrait…',
    groupPlaceholder: 'Your group portrait appears here',
    buttonRetry: 'Try Again',
    buttonErrorDetails: 'Error Details',
    retrying: 'Retrying...',
    maxRetriesReached: 'Max retries reached',
    retryAttempts: 'Retry attempts: {count}',
    printingLockedPopupTitle:
      'Please call the nearest staff member to print your portraits.',
    generationPendingTitle: 'Some portraits are still being created',
    generationPendingCaption:
      '{count} portraits are still generating. Wait to see them all?',
    generationPendingWait: 'Wait for all',
    generationPendingSendReady: 'Send {count} ready portraits',
    printingLockedPopupCaption:
      'Printing at the kiosk is unavailable while no staff member is present.',
  },
  PortraitsGroup: {
    buttonMore: 'Make more portraits',
  },
  PrintLock: {
    lockTitle: 'Lock Printing',
    unlockTitle: 'Unlock Printing',
  },
  PaymentType: {
    title: 'Select a payment method',
    optionCardTitle: 'Bank Card',
    optionCardCaption: 'Visa, Mastercard, & 10+ supported cards',
    optionVoucherTitle: 'Pre-paid Portraits',
    optionVoucherCaption: 'Included in tickets — ask staff to print',
  },
  PaymentTerminal: {
    title: 'Please follow the instructions on the payment terminal',
    titlePaymentSuccess: 'Printing your portraits…',
    titlePaymentError: 'Payment failed',
    titlePrintError1: 'Some portraits failed to print.',
    titlePrintError2: 'Please tap “Print Again” below to retry.',
    buttonRetryPrinting: 'Print Again',
    buttonRetry: 'Try Again',
    buttonCancel: 'Cancel',
    buttonExitSession: 'Start Over',
    buttonBack: 'Go Back',
    total: 'Total:',
    captionPayment: 'Awaiting your payment…',
    captionPrinting: 'Payment successful',
    captionPaymentError:
      'Something went wrong. Please try again or start over.',
    captionPaymentFinal:
      'The payment did not work. Ask a staff member to help you.',
    failureCaptions: {
      notEnoughFunds:
        'There are not enough funds on the card. Try another card.',
      expiredCard: 'This card is expired. Try another card.',
      cardDeclined: 'The card was declined. Try another card.',
      cardError: 'The kiosk cannot use this card. Try another card.',
      processingError:
        'The payment did not complete. Try again with the same card.',
      readerError:
        'The card reader is not available. Ask a staff member to help you.',
      timeout: 'The payment did not complete in time. Try again.',
    },
    errorMessage: 'Unfortunately, we were unable to process your payment',
    popupCancelledTitle: 'Payment Cancelled',
    popupCancelledCaption: 'Would you like to try again or start over?',
    popupButtonContinue: 'Try Again',
  },
  PaymentVoucher: {
    title: 'Please call an employee for assistance to pay with a voucher',
    buttonBack: 'Go Back',
    pinTitle: 'Pay with voucher',
  },
  Printing: {
    title: 'Printing your portraits…',
    titleError: 'Sorry, some portraits failed to print',
    buttonPrintAgain: 'Print Again',
    buttonContinueWithoutPrint: 'Continue without printing',
    errorPartial: '{printed} of {total} printed',
    retrying: 'Trying again…',
    waiting: 'Waiting for the printer…',
    error: 'Oops! Couldn’t print this portrait.',
  },
  PrintFailed: {
    title: 'Sorry, we couldn’t print your photos',
    titlePartial: '{printed} of {total} photos printed',
    caption: 'You can start a new session and create new portraits.',
    captionRefunded: 'We refunded {amount} to your card.',
    captionNotCharged: '{amount} was not charged.',
    captionRefundHelp: 'A staff member will help you with a refund.',
  },
  Success: {
    title: 'Thank you!',
    caption:
      'Your paintings have been printed. Please pick them up at the front desk.',
    buttonStartAgain: 'Start Again',
  },
  GoPhotoScan: {
    title: 'Scan Your PhotoPass',
    subtitle: 'Hold your PhotoPass QR code up to the scanner',
    invalidCode: 'Invalid code format. Please scan again.',
    failedToRegister: 'Failed to register code. Please try again.',
    error: 'An error occurred. Please try again.',
  },

  /**
   * Customer-facing components
   */

  CustomerPhotoFromPhone: {
    title: 'Upload photos from your gallery',
    buttonSelect: 'Select Photos',
    maxCount: '{count} photos max',
    uploading: 'Uploading…',
    uploadError: 'Upload failed. Please check your connection and try again.',
    buttonRetry: 'Try Again',
    successTitle: 'Upload successful',
    successCaption:
      'Please return to the kiosk and follow the on-screen instructions.',
  },
  CustomerPortraitsDownload: {
    title: 'Enter Code to Get Your Portrait',
    buttonSubmit: 'Submit',
    error:
      'This code doesn’t look right. Check the 8-character code printed on your photo and try again.',
    errors: {
      wrongFormat:
        'This code doesn’t look right. Check the 8-character code printed on your photo and try again.',
      notFound:
        "Sorry, no portraits found. Please check the code — it's printed on the front of the portrait, bottom left.",
      serverError:
        "Sorry, we weren't able to retrieve your portraits at this time. Please try again later.",
    },
  },
  CustomerSessionPass: {
    tabs: {
      photos: 'Photos',
      pass: 'QR pass',
    },
    eyebrow: 'Your session pass',
    title: 'Keep this page open',
    description:
      'At the buying station, scan the square code below. Your portraits are available in the Photos tab.',
    descriptionNothingPrinted:
      'At the buying station, scan the square code below to choose and print your photos.',
    scanLabel: 'Scan at the buying station',
    codeLabel: 'Session code',
    ready: 'Ready to scan',
    loading: 'Preparing code',
    unavailable: 'Code unavailable',
    qrAlt: 'QR code for the buying station',
    defaultVenue: 'TheArtisan.ai portrait experience',
  },
  CustomerPortraitsDownloadCode: {
    error: 'Oops! Couldn’t load this portrait.',
    placeholder: 'Loading your portrait…',
    imageAlt: 'Portrait',
    buttonDownload: 'Download',
    buttonSendOrShare: 'Send or Share',
    buttonRetry: 'Try Again',
    shareTitle: 'Your portrait from {clientName}',
    shareText: 'Powered by TheArtisan.ai',
    states: {
      processing:
        'Your portraits are still being prepared. Try again in a moment.',
      partial:
        'Some portraits or page details could not be loaded. Available downloads still work.',
    },
    actions: {
      enterAnotherCode: 'Enter another code',
      contactSupport: 'Contact support',
      retryDownload: 'Retry download',
      downloadFailed: 'Download failed. Check your connection and try again.',
      linkCopied: 'Link copied. You can paste it into a message.',
      shareFailed: 'Sharing is unavailable right now. Please try again.',
    },
  },
  CustomerDigitalExtras: {
    downloadTitle: 'Your photos & video',
    downloadIntro: 'Save your photos and video, or open your QR ticket.',
    photosTitle: 'Your photos',
    photosIntro: 'Save your photos, or open your QR pass.',
    navigation: 'Your downloads',
    tabs: {
      photos: 'Photos',
      extras: 'Video',
      pass: 'QR ticket',
    },
    title: 'Your digital extras',
    loading: 'Loading your digital extras…',
    loadError:
      'Could not load your files. Check your connection and try again.',
    included: 'Included',
    includedOrder:
      'Your video is included. Keep your QR ticket to open it later.',
    preparingHelp:
      'Save this link or keep your QR ticket. You can close this page and return to it later.',
    reference: 'Reference: {reference}',
    retry: 'Try again',
    empty: 'No digital extras have been added to this visit.',
    orderTitle: 'Video order',
    showCashier: 'Show this order and QR ticket to the cashier.',
    videoTotal: 'Video total',
    payAtDesk: 'Payment is handled at the desk.',
    preparing: 'Preparing your files…',
    failed:
      'Your video could not be prepared. Show the reference below to the venue team for help.',
    ready: 'Ready to download',
    video: 'Moving photo',
    videoDescription:
      'A little loop of your favourite moment. Ready for your stories and the family chat.',
    download: 'Download',
    share: 'Share file',
    preparingShare: 'Preparing file…',
    shareFailed: 'Sharing is unavailable. Use Download to save this file.',
  },
  GoPhotoUpload: {
    waitingForPortraits: 'Finishing your portraits',
    waitingForPortraitsSubtitle:
      'Please wait while the remaining portraits are being created.',
    success: 'Photos Uploaded!',
    successSubtitle: 'Your photos are now available on your PhotoPass.',
  },
  system: {
    reconnecting: 'Reconnecting to server…',
  },
};
